<script setup lang="ts">
  defineOptions({
    name: 'ChatConversationOptionsMenu',
  })

  import { computed, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { DotsVerticalIcon, Lock01Icon, Trash01Icon, User01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import CPButton from '@ui/components/CPButton.vue'
  import CPPopover from '@ui/components/CPPopover.vue'

  const props = defineProps({
    triggerId: {
      type: String,
      default: 'conversation-more-options',
    },
    dropdownId: {
      type: String,
      default: '',
    },
    buttonClass: {
      type: String,
      default: '!p-1',
    },
    iconClass: {
      type: String,
      default: 'h-5 w-5 text-secondary',
    },
    placement: {
      type: String as PropType<'bottom-end' | 'bottom-start' | 'top-end' | 'top-start'>,
      default: 'bottom-end',
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: Number,
      default: 10020,
    },
  })

  const emit = defineEmits<{
    block: []
    unblock: []
    viewProfile: []
    deleteChat: []
  }>()

  const { t } = useI18n()

  const isMenuOpen = ref(false)

  const resolvedPopoverId = computed(() => {
    return props.dropdownId || `${props.triggerId}-dropdown`
  })

  const actionItems = computed(() => {
    const blockAction = props.isBlocked
      ? {
          key: 'unblockUser',
          label: t('chats.actions.unblockUser'),
          icon: Lock01Icon,
          danger: false,
        }
      : {
          key: 'blockUser',
          label: t('chats.actions.blockUser'),
          icon: Lock01Icon,
          danger: false,
        }

    return [
      {
        key: 'viewProfile',
        label: t('chats.actions.viewProfile'),
        icon: User01Icon,
        danger: false,
      },
      blockAction,
      {
        key: 'deleteChat',
        label: t('chats.actions.deleteChat'),
        icon: Trash01Icon,
        danger: true,
      },
    ]
  })

  const handleActionClick = (actionKey: string) => {
    isMenuOpen.value = false

    if (actionKey === 'blockUser') {
      emit('block')
      return
    }

    if (actionKey === 'unblockUser') {
      emit('unblock')
      return
    }

    if (actionKey === 'deleteChat') {
      emit('deleteChat')
      return
    }

    if (actionKey === 'viewProfile') {
      emit('viewProfile')
    }
  }
</script>

<template>
  <div class="flex items-center">
    <CPPopover
      :id="resolvedPopoverId"
      trigger="click"
      :show="isMenuOpen"
      :placement="placement"
      :show-arrow="false"
      :z-index="zIndex"
      @update:show="isMenuOpen = $event"
    >
      <template #trigger>
        <CPButton
          :id="triggerId"
          type="text"
          size="sm"
          :class="buttonClass"
          :title="t('chats.actions.moreOptions')"
          :aria-label="t('chats.actions.moreOptions')"
          aria-haspopup="menu"
          :aria-expanded="isMenuOpen"
          :aria-controls="`${resolvedPopoverId}-panel`"
        >
          <DotsVerticalIcon :class="iconClass" />
        </CPButton>
      </template>

      <div :id="`${resolvedPopoverId}-panel`" class="chat-actions-menu py-1">
        <CPButton
          v-for="action in actionItems"
          :id="`${triggerId}-${action.key}-option`"
          :key="action.key"
          type="text"
          size="sm"
          class="chat-actions-menu-item"
          :class="{ 'chat-actions-menu-item-danger': action.danger }"
          @click.stop="handleActionClick(action.key)"
        >
          <div class="flex items-center gap-2">
            <component :is="action.icon" class="h-4 w-4" />
            <span>{{ action.label }}</span>
          </div>
        </CPButton>
      </div>
    </CPPopover>
  </div>
</template>

<style scoped>
  .chat-actions-menu {
    min-width: 10rem;
  }

  .chat-actions-menu-item {
    display: flex;
    flex: 1;
    gap: 0.5rem;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 0.5rem 0.75rem;
    color: var(--cp-brand-font);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    text-align: left;
  }

  .chat-actions-menu-item:hover {
    background: var(--cp-hover);
  }

  .chat-actions-menu-item-danger {
    color: var(--cp-error-500);
  }
</style>
